import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

const state = {
  characters: [],
  currentCharacter: null,
  characterImages: [],
  loading: false,
  error: null
}

const getters = {
  allCharacters: state => state.characters,
  currentCharacter: state => state.currentCharacter,
  characterImages: state => state.characterImages,
  loading: state => state.loading,
  error: state => state.error
}

const actions = {
  async fetchCharacters({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`${API_URL}/characters/`)
      commit('SET_CHARACTERS', response.data)
      commit('SET_ERROR', null)
    } catch (error) {
      console.error('Error fetching characters:', error)
      commit('SET_ERROR', 'Failed to load characters')
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchCharacter({ commit }, id) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`${API_URL}/characters/${id}/`)
      commit('SET_CURRENT_CHARACTER', response.data)
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error fetching character ${id}:`, error)
      commit('SET_ERROR', 'Failed to load character')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchCharacterImages({ commit }, characterId) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`${API_URL}/characters/${characterId}/images/`)
      commit('SET_CHARACTER_IMAGES', response.data)
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error fetching character images for ${characterId}:`, error)
      commit('SET_ERROR', 'Failed to load character images')
      return []
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createCharacterImage({ commit, dispatch }, { characterId, imageData }) {
    commit('SET_LOADING', true)
    try {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append('image', imageData.image)
      
      if (imageData.title) {
        formData.append('title', imageData.title)
      }
      
      if (imageData.description) {
        formData.append('description', imageData.description)
      }
      
      if (imageData.order !== undefined) {
        formData.append('order', imageData.order)
      }

      const response = await axios.post(
        `${API_URL}/characters/${characterId}/images/`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      
      // Refresh character images after creating a new one
      await dispatch('fetchCharacterImages', characterId)
      // Also refresh character details to get updated gallery
      await dispatch('fetchCharacter', characterId)
      
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error creating character image:`, error)
      commit('SET_ERROR', 'Failed to create character image')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateCharacterImage({ commit, dispatch }, { imageId, characterId, imageData }) {
    commit('SET_LOADING', true)
    try {
      // Create FormData for file upload if there's a new image
      const formData = new FormData()
      
      if (imageData.image) {
        formData.append('image', imageData.image)
      }
      
      if (imageData.title !== undefined) {
        formData.append('title', imageData.title)
      }
      
      if (imageData.description !== undefined) {
        formData.append('description', imageData.description)
      }
      
      if (imageData.order !== undefined) {
        formData.append('order', imageData.order)
      }

      const response = await axios.patch(
        `${API_URL}/characters/images/${imageId}/`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      
      // Refresh character images after updating
      await dispatch('fetchCharacterImages', characterId)
      // Also refresh character details to get updated gallery
      await dispatch('fetchCharacter', characterId)
      
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error updating character image:`, error)
      commit('SET_ERROR', 'Failed to update character image')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteCharacterImage({ commit, dispatch }, { imageId, characterId }) {
    commit('SET_LOADING', true)
    try {
      await axios.delete(`${API_URL}/characters/images/${imageId}/`)
      
      // Refresh character images after deleting
      await dispatch('fetchCharacterImages', characterId)
      // Also refresh character details to get updated gallery
      await dispatch('fetchCharacter', characterId)
      
      commit('SET_ERROR', null)
      return true
    } catch (error) {
      console.error(`Error deleting character image:`, error)
      commit('SET_ERROR', 'Failed to delete character image')
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async setCoverImage({ commit, dispatch }, { characterId, imageUrl }) {
    commit('SET_LOADING', true)
    try {
      // Update the character with the new cover image
      const response = await axios.patch(`${API_URL}/characters/${characterId}/`, {
        cover_image: imageUrl
      })
      
      // Refresh character details to get updated cover image
      await dispatch('fetchCharacter', characterId)
      
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error setting cover image:`, error)
      commit('SET_ERROR', 'Failed to set cover image')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async removeCoverImage({ commit, dispatch }, characterId) {
    commit('SET_LOADING', true)
    try {
      // Update the character to remove the cover image
      const response = await axios.patch(`${API_URL}/characters/${characterId}/`, {
        cover_image: null
      })
      
      // Refresh character details to get updated cover image
      await dispatch('fetchCharacter', characterId)
      
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error removing cover image:`, error)
      commit('SET_ERROR', 'Failed to remove cover image')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_CHARACTERS(state, characters) {
    state.characters = characters
  },
  
  SET_CURRENT_CHARACTER(state, character) {
    state.currentCharacter = character
  },
  
  SET_CHARACTER_IMAGES(state, images) {
    state.characterImages = images
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 